import Phaser from 'phaser';
import { GLYPH_SIZE, getGlyphColor } from '../toki/TokiPonaData';

// "kiwen jan" — title glyphs (Blockman)
const TITLE_GLYPHS = [27, 17]; // kiwen, jan
// "o open kepeken nena" — subtitle (press any button to start)
const SUBTITLE_GLYPHS = [68, 71, 25, 64]; // o, open, kepeken, nena

const TITLE_SCALE = 12;
const SUBTITLE_SCALE = 1;
const GAP_TITLE = 0;    // px between title glyphs (at native scale, before scaling)
const GAP_SUBTITLE = 2; // px between subtitle glyphs

export class StartMenuScene extends Phaser.Scene {
    private onStart: (() => void) | null = null;
    private dismissed = false;
    private titleMusic: Phaser.Sound.BaseSound | null = null;

    constructor() {
        super({ key: 'StartMenuScene' });
    }

    init(data: { onStart?: () => void }): void {
        this.onStart = data.onStart || null;
        this.dismissed = false;
        this.titleMusic = null;
    }

    create(): void {
        const { width, height } = this.cameras.main;

        // Dark overlay (greys out the game world behind)
        const overlay = this.add.rectangle(0, 0, width, height, 0x000000, 0.9);
        overlay.setOrigin(0, 0).setDepth(0);

        // Title music — audio is already unlocked by SplashScene.
        // Audio may still be loading in background, so try now and also listen for load complete.
        this.tryPlayTitleMusic();
        const gameScene = this.scene.get('GameScene');
        if (gameScene) {
            gameScene.load.once('complete', () => this.tryPlayTitleMusic());
        }

        // Title: "kiwen jan" at 12x scale, colored, centered vertically
        const titleGlyphSize = GLYPH_SIZE * TITLE_SCALE;
        const titleTotalWidth = TITLE_GLYPHS.length * titleGlyphSize + (TITLE_GLYPHS.length - 1) * GAP_TITLE * TITLE_SCALE;
        const titleStartX = (width - titleTotalWidth) / 2;
        const titleY = height / 2;

        for (let i = 0; i < TITLE_GLYPHS.length; i++) {
            const idx = TITLE_GLYPHS[i];
            const x = titleStartX + i * (titleGlyphSize + GAP_TITLE * TITLE_SCALE) + titleGlyphSize / 2;
            const glyph = this.add.image(x, titleY, 'toki_glyphs', idx);
            glyph.setScale(TITLE_SCALE);
            glyph.setTint(getGlyphColor(idx));
            glyph.setDepth(1);
        }

        // Subtitle: "o open kepeken nena" at 1x scale, white, near bottom
        const subtitleGlyphSize = GLYPH_SIZE * SUBTITLE_SCALE;
        const subtitleTotalWidth = SUBTITLE_GLYPHS.length * subtitleGlyphSize + (SUBTITLE_GLYPHS.length - 1) * GAP_SUBTITLE;
        const subtitleStartX = (width - subtitleTotalWidth) / 2;
        const subtitleY = height - 20;

        for (let i = 0; i < SUBTITLE_GLYPHS.length; i++) {
            const idx = SUBTITLE_GLYPHS[i];
            const x = subtitleStartX + i * (subtitleGlyphSize + GAP_SUBTITLE) + subtitleGlyphSize / 2;
            const glyph = this.add.image(x, subtitleY, 'toki_glyphs', idx);
            glyph.setScale(SUBTITLE_SCALE);
            glyph.setTint(0xffffff);
            glyph.setDepth(1);
        }

        // Dismiss on any key or click
        this.input.keyboard!.on('keydown', () => this.dismiss());
        this.input.on('pointerdown', () => this.dismiss());
    }

    private tryPlayTitleMusic(): void {
        if (this.titleMusic || this.dismissed) return;
        if (!this.cache.audio.exists('title_music')) return;
        this.titleMusic = this.sound.add('title_music', { loop: true, volume: 0 });
        this.titleMusic.play();
        this.tweens.add({
            targets: this.titleMusic,
            volume: { from: 0, to: 0.1 },
            duration: 1500,
            ease: 'Linear',
        });
    }

    dismiss(): void {
        if (this.dismissed) return;
        this.dismissed = true;
        // Stop title music immediately
        if (this.titleMusic) {
            this.titleMusic.stop();
            this.titleMusic.destroy();
            this.titleMusic = null;
        }
        this.onStart?.();
        this.scene.stop();
    }
}
